import React from 'react';
import Icon from '../../../components/AppIcon';

const ProjectTimeline = ({ phases = [], currentPhaseId }) => {
  const getPhaseStatus = (phase) => {
    // Respect the actual phase status from the database
    if (phase?.status === 'completed') return 'completed';
    if (phase?.status === 'ongoing') return 'current';
    if (phase?.id === currentPhaseId && phase?.status !== 'pending') return 'current';
    return 'pending';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'current':
        return 'bg-blue-500';
      default:
        return 'bg-gray-300';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-700';
      case 'current':
        return 'text-blue-700';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50';
      case 'current':
        return 'bg-blue-50';
      default:
        return 'bg-gray-50';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold text-foreground mb-6">Project Timeline</h3>
      <div className="space-y-4">
        {phases?.map((phase, index) => {
          const status = getPhaseStatus(phase);
          const isLast = index === phases?.length - 1;
          
          return (
            <div key={phase?.id} className="relative">
              <div className="flex items-start gap-4">
                {/* Timeline dot and line */}
                <div className="flex flex-col items-center">
                  <div className={`w-4 h-4 rounded-full ${getStatusColor(status)} flex items-center justify-center`}>
                    {status === 'completed' && (
                      <Icon name="Check" size={10} className="text-white" strokeWidth={3} />
                    )}
                  </div>
                  {!isLast && (
                    <div className={`w-0.5 h-12 mt-1 ${status === 'completed' ? 'bg-green-300' : 'bg-gray-200'}`} />
                  )}
                </div>

                {/* Phase info */}
                <div className={`flex-1 pb-4 px-4 py-3 rounded-lg ${getStatusBgColor(status)}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className={`font-semibold ${getStatusTextColor(status)}`}>
                        {phase?.phase_name || phase?.name}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {phase?.planned_start_date && phase?.planned_end_date ? (
                          `${new Date(phase?.planned_start_date)?.toLocaleDateString()} - ${new Date(phase?.planned_end_date)?.toLocaleDateString()}`
                        ) : (
                          'Dates not set'
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)} text-white`}>
                        {status === 'completed' ? 'Completed' : status === 'current' ? 'In Progress' : 'Pending'}
                      </span>
                      {phase?.progress !== undefined && (
                        <p className="text-sm text-muted-foreground mt-1">{phase?.progress}% complete</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-border flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-muted-foreground">Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-muted-foreground">Current</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-gray-300" />
          <span className="text-muted-foreground">Pending</span>
        </div>
      </div>
    </div>
  );
};

export default ProjectTimeline;